import type { PillarPosition } from '../types/pillars';
import type { TenGod, TenGodMapping } from '../types/ten-gods';
import type { GyeokGukAnalysis } from '../types/gyeok-guk';
import type { YongSinResult } from '../types/strength';
import type {
  TenGodNature,
  TenGodCategory,
  TenGodCombination,
  MissingCategoryInfo,
  TongbyeonAnalysis,
} from '../types/tongbyeon';

/** 십신 → 길흉 정적 맵 */
const TEN_GOD_NATURE: Record<TenGod, TenGodNature> = {
  '비견': '흉',
  '겁재': '흉',
  '식신': '길',
  '상관': '흉',
  '정재': '길',
  '편재': '흉',
  '정관': '길',
  '편관': '흉',
  '정인': '길',
  '편인': '흉',
};

/** 십신 → 카테고리 매핑 */
function tenGodToCategory(tg: TenGod): TenGodCategory {
  switch (tg) {
    case '비견': case '겁재': return '비겁';
    case '식신': case '상관': return '식상';
    case '정재': case '편재': return '재성';
    case '정관': case '편관': return '관성';
    case '정인': case '편인': return '인성';
  }
}

/** 부재 카테고리별 설명 */
const MISSING_DESCRIPTIONS: Record<TenGodCategory, string> = {
  '비겁': '뚝심/배짱/자신감이 부족하고 의존적, 소극적인 사회성을 보일 수 있음',
  '식상': '행동력과 말주변이 부족하고, 과정을 생략하고 결론을 원하는 기질',
  '재성': '목표감/끈기 부족, 금전관리가 계획적이지 못할 수 있음',
  '관성': '객관성이 결여되고 자신을 통제하는 능력이 부족할 수 있음',
  '인성': '단순하고 순진한 편. 복잡한 것을 싫어하지만 인내와 면역력이 생김',
};

/** 사주 전체에서 존재하는 십신 set 수집 */
function collectTenGods(
  tenGods: TenGodMapping,
  noHour?: boolean,
): { tenGodSet: Set<TenGod>; tenGodCounts: Map<TenGod, number> } {
  const tenGodSet = new Set<TenGod>();
  const tenGodCounts = new Map<TenGod, number>();

  const positions: PillarPosition[] = noHour ? ['year', 'month', 'day'] : ['year', 'month', 'day', 'hour'];

  // 천간 십신
  for (const pos of positions) {
    if (pos === 'day') continue;
    const info = tenGods[pos];
    if (info && 'tenGod' in info && info.tenGod) {
      tenGodSet.add(info.tenGod);
      tenGodCounts.set(info.tenGod, (tenGodCounts.get(info.tenGod) || 0) + 1);
    }
  }

  // 지장간 십신
  for (const pos of positions) {
    const hidden = tenGods.hiddenStems[pos];
    if (hidden) {
      for (const h of hidden) {
        if (h.tenGod) {
          tenGodSet.add(h.tenGod);
          tenGodCounts.set(h.tenGod, (tenGodCounts.get(h.tenGod) || 0) + 1);
        }
      }
    }
  }

  return { tenGodSet, tenGodCounts };
}

/** 카테고리별 개수 집계 */
function countByCategory(tenGodCounts: Map<TenGod, number>): Map<TenGodCategory, number> {
  const catCounts = new Map<TenGodCategory, number>();
  for (const [tg, count] of tenGodCounts) {
    const cat = tenGodToCategory(tg);
    catCounts.set(cat, (catCounts.get(cat) || 0) + count);
  }
  return catCounts;
}

/**
 * 통변 분석
 *
 * - 십신 길흉 분류
 * - 핵심 조합 규칙 8종 탐지
 * - 부재 십신 카테고리 분석
 */
export function analyzeTongbyeon(
  tenGods: TenGodMapping,
  gyeokGuk?: GyeokGukAnalysis,
  noHour?: boolean,
  yongSinMethods?: YongSinResult[],
): TongbyeonAnalysis {
  const { tenGodSet, tenGodCounts } = collectTenGods(tenGods, noHour);
  const catCounts = countByCategory(tenGodCounts);

  // 1. 십신 길흉 분류 (정적 맵)
  const tenGodNatures = { ...TEN_GOD_NATURE };

  // 2. 조합 규칙 탐지
  const combinations: TenGodCombination[] = [];

  const has = (tg: TenGod) => tenGodSet.has(tg);
  const catCount = (cat: TenGodCategory) => catCounts.get(cat) || 0;
  /** 존재하는 십신만 모아서 involved 배열 생성 */
  const collectInvolved = (...gods: TenGod[]): TenGod[] => gods.filter(has);

  // 식신제살: 식신 + 편관 공존 → 편관의 해를 식신이 제어
  if (has('식신') && has('편관')) {
    combinations.push({
      name: '식신제살',
      description: '식신이 편관(칠살)을 제어하여 전화위복의 구조',
      involved: ['식신', '편관'],
      effect: '길',
    });
  }

  // 상관패인: 상관 + 정인 공존 → 정인이 상관을 교화
  if (has('상관') && has('정인')) {
    combinations.push({
      name: '상관패인',
      description: '정인이 상관을 교화하여 교육/언론/예체능 적성',
      involved: ['상관', '정인'],
      effect: '길',
    });
  }

  // 도식(효신탈식): 편인 + 식신 공존 → 편인이 식신을 파괴
  if (has('편인') && has('식신')) {
    combinations.push({
      name: '도식',
      description: '편인이 식신을 극하여 의식주와 생기를 해칠 수 있음',
      involved: ['편인', '식신'],
      effect: '흉',
    });
  }

  // 군비쟁재: 비겁 3개+ AND 재성 존재
  if (catCount('비겁') >= 3 && catCount('재성') > 0) {
    combinations.push({
      name: '군비쟁재',
      description: '비겁이 과다하여 재성을 극하고 재물 손실 위험',
      involved: ['비견', '겁재'],
      effect: '흉',
    });
  }

  // 관인상생: 관성 + 인성 공존 → 이상적 경로
  if (catCount('관성') > 0 && catCount('인성') > 0) {
    combinations.push({
      name: '관인상생',
      description: '관성이 인성을 생하고 인성이 일간을 생하는 이상적 구조',
      involved: collectInvolved('정관', '편관', '정인', '편인'),
      effect: '길',
    });
  }

  // 양인합살: 양인격(격국) + 편관 존재
  if (gyeokGuk?.type === '양인격' && has('편관')) {
    combinations.push({
      name: '양인합살',
      description: '양인(겁재)과 편관이 적수를 만나 특수직/무관에 적합',
      involved: ['겁재', '편관'],
      effect: '길',
    });
  }

  // 재생살: 재성 + 편관 공존 → 재성이 편관을 생
  if (catCount('재성') > 0 && has('편관')) {
    const hasJesal = has('식신'); // 식신제살 여부로 길흉 분기
    combinations.push({
      name: '재생살',
      description: hasJesal
        ? '재성이 편관을 생하나 식신제살로 제어되어 긍정적'
        : '재성이 편관을 생하여 일간에 부담이 가중됨',
      involved: collectInvolved('정재', '편재', '편관'),
      effect: hasJesal ? '길' : '흉',
    });
  }

  // 탐재괴인: 재성 + 인성 공존 AND 재성이 인성보다 우세
  if (catCount('재성') > 0 && catCount('인성') > 0 && catCount('재성') > catCount('인성')) {
    combinations.push({
      name: '탐재괴인',
      description: '재성이 인성을 극하여 학문/도덕보다 재물에 치중',
      involved: collectInvolved('정재', '편재', '정인', '편인'),
      effect: '흉',
    });
  }

  // 3. 부재 십신 카테고리 분석
  const missingCategories: MissingCategoryInfo[] = [];
  const allCategories: TenGodCategory[] = ['비겁', '식상', '재성', '관성', '인성'];
  for (const cat of allCategories) {
    if (catCount(cat) === 0) {
      missingCategories.push({
        category: cat,
        description: MISSING_DESCRIPTIONS[cat],
      });
    }
  }

  // 전왕 패턴: 병약법 결과에서 추출
  const byeongyak = yongSinMethods?.find(m => m.method === '병약');
  const jeonwangPattern = byeongyak?.jeonwangPattern;

  return {
    tenGodNatures,
    combinations,
    missingCategories,
    jeonwangPattern,
  };
}
